import { fraudBrowserSDK } from "@mozn-sdk/focal-fraud";
import {
	BROWSER_FINGERPRINT_SDK_API_KEY,
	BROWSER_FINGERPRINT_SDK_API_URL,
} from "@/constants/constants";
import { clearVendorStorage } from "@/services/consent/storage-cleanup";

type BrowserSignalsOptions = Omit<
	Parameters<typeof fraudBrowserSDK>[0],
	"apiKey" | "apiUrl"
>;

// `_fp_id` (sessionStorage) and `device_fingerprint` (cookie) hold the device
// id; `__sdk_acd_` are the SDK's storage-capability probes.
const FRAUD_SDK_STORAGE_PREFIXES = [
	"_fp_id",
	"device_fingerprint",
	"__sdk_acd_",
] as const;

let hasConsent = false;
let pendingOptions: BrowserSignalsOptions | null = null;

const isFraudSdkEnabled = () => Boolean(BROWSER_FINGERPRINT_SDK_API_KEY);

const runSdk = (options: BrowserSignalsOptions) => {
	fraudBrowserSDK({
		...options,
		apiKey: BROWSER_FINGERPRINT_SDK_API_KEY,
		apiUrl: BROWSER_FINGERPRINT_SDK_API_URL,
	}).catch(() => {
		// Intentionally ignored: a failed collection must never break the app.
	});
};

/**
 * Collects device and browser signals. Without consent the request is held
 * (latest wins) and runs once consent is granted, so a user who accepts
 * mid-session is still fingerprinted for that session.
 */
const collectBrowserSignals = (options: BrowserSignalsOptions) => {
	if (!isFraudSdkEnabled()) return;
	if (!hasConsent) {
		pendingOptions = options;
		return;
	}
	runSdk(options);
};

/** Called by the consent service once the fraud-prevention category is accepted. */
const grantFraudSdkConsent = () => {
	hasConsent = true;
	if (!pendingOptions) return;
	runSdk(pendingOptions);
	pendingOptions = null;
};

/** Deletes the stored device id and probes; the consent service then reloads. */
const revokeFraudSdkConsent = () => {
	hasConsent = false;
	pendingOptions = null;
	clearVendorStorage(FRAUD_SDK_STORAGE_PREFIXES);
};

export {
	collectBrowserSignals,
	grantFraudSdkConsent,
	isFraudSdkEnabled,
	revokeFraudSdkConsent,
};
export type { BrowserSignalsOptions };
